import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { ListViewBase } from "../../../../components/listView";
import PaginationButtons from "../../../../components/itemsViewHeader/paginationButtons/PaginationButtons";
import { PaginationFrame } from "../../../../components/pagination/PaginationFrame";
import { PerformanceTabSection } from "features/Library/Common/Performance/PerformanceTabSection/PerformanceTabSection";
import { RequestStatusRenderer } from "components/requestStatsRenderer/RequestStatusRenderer";
import { type PerformanceFilter, noData, noTableData } from "features/Library/Common/utils/performanceUtils";
import type SortDirections from "enums/sortDirections";
import { type ColumnOption } from "../../../../interfaces/columnOptions";
import { buildTableBody, createSelectionChangeHandler } from "./genericPerformanceListUtils";
import { type PaginationParams } from "features/Library/Common/models";

export interface FetchPageResult<T> {
  rows: T[];
  totalCount: number;
}

export type FetchPageFn<T> = (params: PaginationParams, signal?: AbortSignal) => Promise<FetchPageResult<T>>;

export type ExportAvailability = {
  hasDataToExport: boolean;
};

export interface Props<T extends object> {
  columns: ColumnOption<T>[];
  filter: PerformanceFilter;
  fetchPage: FetchPageFn<T>;
  queryKeyBase: readonly unknown[];
  title?: string;
  hideControls?: boolean;
  withSelection?: boolean;
  onSelectionChanged?: (data: T[]) => void;
  hasDataForExport?: (state: ExportAvailability) => void;
  isDateAllTime?: boolean;
}

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_SKIP_COUNT = 0;

type SortableColumnOption<T> = ColumnOption<T> & { sortKey?: string };

export const GenericPerformanceListWithServerPagination = <T extends object>({
  columns,
  filter,
  fetchPage,
  queryKeyBase,
  hideControls,
  title = "",
  withSelection = false,
  onSelectionChanged,
  hasDataForExport,
  isDateAllTime,
}: Props<T>) => {
  const [selectedItems, setSelectedItems] = useState<T[]>([]);
  const [params, setParams] = useState<PaginationParams>(() => ({
    skip: DEFAULT_SKIP_COUNT,
    top: DEFAULT_PAGE_SIZE,
    sortBy: undefined,
    sortOrder: undefined,
  }));

  const [uiSortColumn, setUiSortColumn] = useState<string | undefined>(undefined);
  const [uiSortDirection, setUiSortDirection] = useState<SortDirections | undefined>(undefined);

  // Map column names to their definitions so UI labels can translate to backend sort fields.
  const columnLookup = useMemo(() => {
    const byName = new Map<string, SortableColumnOption<T>>();

    columns.forEach((column) => {
      const sortableColumn = column as SortableColumnOption<T>;
      byName.set(column.name.toLowerCase(), sortableColumn);
    });

    return byName;
  }, [columns]);

  // Convert a UI column name into the sort key expected by the server.
  const resolveSortKey = useCallback(
    (columnName?: string) => {
      if (!columnName) {
        return undefined;
      }

      return columnLookup.get(columnName.toLowerCase())?.sortKey ?? columnName;
    },
    [columnLookup],
  );

  // Reset pagination when the query key changes.
  useEffect(() => {
    setParams((prev) => {
      if (prev.skip === DEFAULT_SKIP_COUNT) {
        return prev;
      }

      return { ...prev, skip: DEFAULT_SKIP_COUNT };
    });

    setSelectedItems((prev) => (prev.length ? [] : prev));
  }, [queryKeyBase]);

  // Track which rows are selected when selection is enabled.
  const handleSelectionChanged = createSelectionChangeHandler(setSelectedItems);

  useEffect(() => {
    onSelectionChanged?.(selectedItems);
  }, [onSelectionChanged, selectedItems]);

  // Build the React Query cache key so pagination and sorting changes trigger refetches.
  const queryKey = useMemo(() => [...queryKeyBase, params], [queryKeyBase, params]);

  const pageQuery = useQuery({
    queryKey,
    queryFn: ({ signal }) => fetchPage(params, signal),
    keepPreviousData: true,
  });

  const items = pageQuery.data?.rows ?? [];
  const totalCount = pageQuery.data?.totalCount ?? 0;
  const pageSize = params.top || DEFAULT_PAGE_SIZE;
  const currentPage = pageSize > 0 ? Math.floor(params.skip / pageSize) + 1 : 1;
  const pageRange = {
    from: totalCount === 0 ? 0 : params.skip + 1,
    to: totalCount === 0 ? 0 : Math.min(params.skip + pageSize, totalCount),
  };

  useEffect(() => {
    hasDataForExport?.({ hasDataToExport: totalCount > 0 });
  }, [hasDataForExport, totalCount]);

  // Adjust pagination by stepping forward or backward one page depending on the direction provided.
  const adjustPage = useCallback(
    (direction: -1 | 1) => {
      setParams((prev) => {
        const top = prev.top || DEFAULT_PAGE_SIZE;
        const nextSkip = direction === -1 ? Math.max(0, prev.skip - top) : prev.skip + top;

        if ((direction === 1 && nextSkip >= totalCount) || nextSkip === prev.skip) {
          return prev;
        }

        return { ...prev, skip: nextSkip };
      });
    },
    [totalCount],
  );

  // Move the pagination backward by one page when the previous button is pressed.
  const handlePrevPage = useCallback(() => adjustPage(-1), [adjustPage]);

  // Advance the pagination forward by one page when the next button is pressed.
  const handleNextPage = useCallback(() => adjustPage(1), [adjustPage]);

  // Send updated pagination and sort preferences to the server and update UI state accordingly.
  const handleListRequest = useCallback(
    (skip: number, top: number, sortingColumnName?: string, sortingDirection?: SortDirections) => {
      setParams((prev) => {
        const nextSortBy = sortingColumnName ? resolveSortKey(sortingColumnName) : prev.sortBy;
        const nextSortOrder = sortingDirection ?? prev.sortOrder;
        const nextParams: PaginationParams = {
          skip,
          top,
          sortBy: nextSortBy,
          sortOrder: nextSortOrder,
        };

        if (
          prev.skip === nextParams.skip &&
          prev.top === nextParams.top &&
          prev.sortBy === nextParams.sortBy &&
          prev.sortOrder === nextParams.sortOrder
        ) {
          return prev;
        }

        return nextParams;
      });

      if (sortingColumnName) {
        setUiSortColumn(sortingColumnName.toLowerCase());
      }

      if (sortingDirection) {
        setUiSortDirection(sortingDirection);
      }
    },
    [resolveSortKey],
  );

  // Render table cells for each row using the shared builder.
  const buildTableBodyCells = useCallback((item: T) => buildTableBody(item, columns), [columns]);

  return (
    <PerformanceTabSection
      hideControls={hideControls}
      className="performance-tab-people"
      title={<span className="main-title-text">{title}</span>}
      controls={
        <>
          <PaginationFrame range={pageRange} count={totalCount} />
          <PaginationButtons
            onPrevPage={handlePrevPage}
            onNextPage={handleNextPage}
            itemsInlineCount={totalCount}
            itemsPageRange={pageRange}
            activePage={currentPage}
          />
        </>
      }
      content={
        <RequestStatusRenderer state={pageQuery.status}>
          {noTableData(items) ? (
            noData(filter, isDateAllTime)
          ) : (
            <ListViewBase
              columnOptions={columns}
              loadPage={handleListRequest}
              items={items}
              itemsAmount={totalCount}
              buildTableBody={buildTableBodyCells}
              withFooter={false}
              activePage={currentPage}
              withSelection={withSelection}
              onSelectionChanged={handleSelectionChanged}
              sortingColumnName={uiSortColumn}
              sortingDirection={uiSortDirection}
              paginateOnLoad
              isLoading={pageQuery.isFetching}
            />
          )}
        </RequestStatusRenderer>
      }
    />
  );
};
